import { Axios } from "@/utils/axios";
import { aes256Encrypt } from "@/utils/crypto";
import dayjs from "dayjs";

export type ProductKey = string;
export type Gender = "남자" | "여자" | "";

type DeviceType = "pc" | "mobile";

export interface CardInfo {
  card_no: string; // 16자리 (하이픈 제거)
  expiry: string; // MMYY 형식 4자리
  birth_or_biz_no: string; // 생년월일 6자리 또는 사업자번호 10자리
  password: string; // 비밀번호 앞 2자리
  owner_type: "0" | "1"; // "0": 개인, "1": 법인
}

interface MonthlyPayAcount {
  number: number;
  package: ProductKey;
  coupon?: string | null;
}

interface MonthlyPayOpitionalAgreement {
  is_personal_information_collect_use_possible: boolean;
  is_marketing_call_possible: boolean;
  is_marketing_sms_possible: boolean;
  is_marketing_email_possible: boolean;
}

type TemporaryAdvancePayCustomerData = {
  name: string;
  phone: string;
  email?: string;
  address_postal?: string;
  address_detail?: string;
  postal_code?: string;
  birth_date: string;
  gender?: Gender;
  memo?: string;
  registered_referrer?: string;
  goi_fp_id?: string;
  kakaobank?: string;
  kuid?: string;
};

export type PostMonthlyPaymentBody = {
  connecting_information?: string;
  device_type: DeviceType;
  account: MonthlyPayAcount;
  optional_agreement: MonthlyPayOpitionalAgreement;
  card_info?: CardInfo;
  registration_channel?: "DIRECT_API" | "AUTH_WINDOW";
  temporary_customer_id?: string;
} & TossPaymentRequestBody &
  TemporaryAdvancePayCustomerData;

export type TossPaymentRequestBody = {
  payment_key?: string;
  order_id?: string;
  adison_click_key?: string | null;
};

type AdvancePayPackages = {
  /** 현재 시점 판매 유/무 */
  is_on_sale: boolean;
  /** 정기결제인 경우, 일시납 x */
  is_payment_repetitive: boolean;
  name: string;
  pay_amount: number;
};

const encryptPassword = (password: string) => {
  const now = dayjs().utc();
  const formattedDate = now.format("YYYY-MM-DD HH:mm:ss");

  return aes256Encrypt(`${formattedDate}|${password}`);
};

export const temporaryAdvancePayCustomers = async (
  body: PostMonthlyPaymentBody
) => {
  const postBody = { ...body, password: encryptPassword(body.birth_date) };
  return await Axios.post("/temporary_advance_pay_customers/", postBody);
};

export const getTemporaryAdvancePayTransactions = async (order_id: string) => {
  return await Axios.get(`/temporary_advance_pay_transactions/${order_id}/`);
};

export const postTemporaryAdvancePayCustomersCheck = async ({
  key,
}: {
  key: string;
}) => {
  const body = { password: encryptPassword(key) };
  return await Axios.post(
    `/temporary_advance_pay_customers/${key}/check/`,
    body
  );
};

export const getAdvancePayPackages = async (key: string) => {
  const response = await Axios.get<AdvancePayPackages>(
    `/advance_pay_packages/${key}/`
  );

  return response;
};

export const patchAdvancePayCustomerMemo = async ({
  key,
  birthDate,
  memo,
}: {
  key: string;
  birthDate: string;
  memo: string;
}) => {
  const body = { password: encryptPassword(birthDate), memo };
  return await Axios.patch(`/advance_pay_customers/${key}/`, body);
};

/** Lead.status 축약값. 온보딩 임박도 저장용. */
export type AdvancePayCustomerUrgency = "여유" | "1달" | "임박" | "임종상태";

/**
 * 배너·온보딩·견적 접수의 대상이 되는 임박도.
 *
 * 원본 유니온에서 Extract 로 파생시켜 두면 AdvancePayCustomerUrgency 가
 * 바뀔 때 이 타입도 같이 줄어들고, 그 값을 처리하던 코드가 소비처에서 드러난다.
 * 리터럴을 직접 적으면 원본에 없는 값을 요구하는 타입이 조용히 남는다.
 */
export type UrgentUrgency = Extract<AdvancePayCustomerUrgency, "1달" | "임박">;

/** "여유" 와 "임종상태" 는 배너도 온보딩도 대상이 아니다. */
export const isUrgentUrgency = (
  value: AdvancePayCustomerUrgency | null
): value is UrgentUrgency => value === "1달" || value === "임박";

/**
 * 프라임 가입 직후 온보딩 임박도 저장.
 * PATCH /advance_pay_customers/{key}/ — password + urgency (재선택 시 덮어쓰기 가능)
 */
export const patchAdvancePayCustomerUrgency = async ({
  key,
  birthDate,
  urgency,
}: {
  key: string;
  birthDate: string;
  urgency: AdvancePayCustomerUrgency;
}) => {
  const body = { password: encryptPassword(birthDate), urgency };
  return await Axios.patch(`/advance_pay_customers/${key}/`, body);
};

export const postAdvancePayCustomerReferral = async ({
  key,
  birthDate,
  name,
  phone,
  organizationName,
  organizationNumber,
}: {
  key: string;
  birthDate: string;
  name: string;
  phone: string;
  organizationName: string;
  organizationNumber: string;
}) => {
  const body = {
    password: encryptPassword(birthDate),
    name,
    phone,
    organizationName,
    organizationNumber,
  };
  return await Axios.post(`/advance_pay_customers/${key}/referral/`, body);
};

export interface HectoPayAuthFormDataRequest {
  name: string;
  phone: string;
  birth_date: string;
  email?: string;
  address_postal?: string;
  address_detail?: string;
  postal_code?: string;
  gender?: Gender;
  /** 서버에서 진행 */
  // cancel_url: string;
  device_type?: DeviceType;
  account: MonthlyPayAcount;
  optional_agreement: MonthlyPayOpitionalAgreement;
  memo?: string;
  connecting_information?: string;
  registered_referrer?: string;
  goi_fp_id?: string;
  query_params?: Record<string, any>;
  adison_click_key?: string;
  kakaobank?: string;
  kuid?: string;
}

export const postHectoPayAuthFormData = async (
  body: HectoPayAuthFormDataRequest
) => {
  const postBody = { ...body, password: encryptPassword(body.birth_date) };

  return await Axios.post(
    "/temporary_advance_pay_customers/hectopay_auth_form_data/",
    postBody
  );
};
